<template>
  <v-card flat class="donut-chart-card">
    <h3 class="card-title">{{ settings.title }}</h3>

    <div class="body">
      <div class="chart-holder">
        <Doughnut :data="chartData" :options="chartOptions" :style="chartStyle" />
        <div class="centre">
          <div class="centre-value">{{ displayCentreValue }}</div>
          <div class="centre-label">{{ settings.centreLabel }}</div>
        </div>
      </div>

      <div class="legend-column">
        <ul class="legend">
          <li v-for="(s, i) in resolvedSegments" :key="i">
            <span class="swatch" :style="{ backgroundColor: s.color }"></span>
            <span class="legend-text">
              <strong>{{ percent(s.value) }}%</strong>
              <span class="count">({{ s.value }})</span>
              {{ s.label }}
            </span>
          </li>
          <li v-if="!resolvedSegments.length && !isLoading" class="empty">
            No data.
          </li>
          <li v-if="isLoading" class="empty">Loading…</li>
        </ul>

        <div class="footer" v-if="settings.linkText">
          <a class="view-link" href="#" @click.prevent="onLinkClick">
            {{ settings.linkText }}
          </a>
        </div>
      </div>
    </div>
  </v-card>
</template>

<script>
import axios from "axios";
import { Doughnut } from "vue-chartjs";

const DEFAULT_PALETTE = [
  "#FFCB00",
  "#EA3785",
  "#2B9DBF",
  "#2cd946",
  "#ED742D",
  "#6938b9",
  "#50B3CB",
  "#D9D9D9",
];

/**
 * Generic donut chart with optional dynamic data source.
 *
 * Dual-mode:
 *  - Standalone:
 *      <cDonutChart
 *        title="Job Evaluation Progress"
 *        :segments="[{ label, value, color }, ...]"
 *        centre-label="Jobs"
 *        link-text="View Job Evaluations" />
 *
 *  - Landing-config (controlType: 'DonutChart'):
 *      definition: {
 *        controlType: 'DonutChart',
 *        title, centreLabel, centreValue, linkText, linkUrl,
 *        dataSource: 'static' | 'api',
 *        // when dataSource === 'api':
 *        apiEndpoint: 'report/evaluationProgress',
 *        apiMethod: 'GET' | 'POST',
 *        apiParams: [{ name, value }],
 *        segmentMapping: { labelField, valueField, colourField },
 *        segmentColours: [{ label, colour }],
 *        // when dataSource === 'static':
 *        segments: [{ label, value, color }]
 *      }
 *
 * API response contract:
 *  - Either an array of rows, or { Status: 'OK', Data: [rows] }
 *  - Each row must contain at least labelField and valueField as configured
 *
 * Emits:
 *  - linkClick      — footer link clicked (payload: { url })
 *  - sessionExpired — on 401
 */
export default {
  name: "cDonutChart",
  components: { Doughnut },
  props: {
    // landing-config mode
    definition: { type: Object, default: null },
    editMode: { type: Boolean, default: false },
    qaActions: { type: Array, default: () => [] },
    // standalone mode
    title: { type: String, default: "" },
    segments: { type: Array, default: null },
    centreLabel: { type: String, default: "" },
    centreValue: { type: [String, Number], default: null },
    linkText: { type: String, default: "" },
    linkUrl: { type: String, default: null },
  },
  data() {
    return {
      apiSegments: [],
      isLoading: false,
    };
  },
  computed: {
    settings() {
      const d = this.definition || {};
      return {
        title: d.title ?? this.title,
        segments: d.segments ?? null,
        centreLabel: d.centreLabel ?? this.centreLabel,
        centreValue: d.centreValue ?? this.centreValue,
        linkText: d.linkText ?? this.linkText,
        linkUrl: d.linkUrl ?? this.linkUrl,
        dataSource: d.dataSource ?? "static",
        apiEndpoint: d.apiEndpoint ?? null,
        apiMethod: d.apiMethod ?? "POST",
        apiParams: d.apiParams ?? [],
        apiFilters: d.apiFilters ?? [],
        segmentMapping: d.segmentMapping ?? {
          labelField: "label",
          valueField: "value",
          colourField: "colour",
        },
        segmentColours: d.segmentColours ?? [],
      };
    },
    resolvedSegments() {
      if (this.settings.dataSource === "api") return this.apiSegments;
      if (this.settings.segments && this.settings.segments.length)
        return this.settings.segments;
      if (this.segments && this.segments.length) return this.segments;
      return [];
    },
    totalValue() {
      return this.resolvedSegments.reduce(
        (sum, s) => sum + (Number(s.value) || 0),
        0
      );
    },
    displayCentreValue() {
      if (this.settings.centreValue != null && this.settings.centreValue !== "")
        return this.settings.centreValue;
      return this.totalValue;
    },
    chartData() {
      return {
        labels: this.resolvedSegments.map((s) => s.label),
        datasets: [
          {
            data: this.resolvedSegments.map((s) => Number(s.value) || 0),
            backgroundColor: this.resolvedSegments.map((s) => s.color),
            borderWidth: 0,
          },
        ],
      };
    },
    chartOptions() {
      return {
        responsive: false,
        maintainAspectRatio: true,
        cutout: "72%",
        plugins: {
          legend: { display: false },
          tooltip: { enabled: true },
        },
      };
    },
    chartStyle() {
      return { width: "180px", height: "180px" };
    },
    /** Key used by watcher to refetch when anything API-related changes. */
    apiFetchKey() {
      return JSON.stringify({
        ds: this.settings.dataSource,
        ep: this.settings.apiEndpoint,
        m: this.settings.apiMethod,
        p: this.settings.apiParams,
        f: this.settings.apiFilters,
      });
    },
  },
  watch: {
    apiFetchKey: {
      immediate: true,
      handler() {
        if (this.settings.dataSource === "api" && this.settings.apiEndpoint) {
          this.fetchData();
        } else {
          this.apiSegments = [];
        }
      },
    },
  },
  methods: {
    percent(value) {
      if (!this.totalValue) return 0;
      return Math.round((Number(value) / this.totalValue) * 100);
    },
    onLinkClick() {
      this.$emit("linkClick", { url: this.settings.linkUrl });
      if (this.settings.linkUrl && !this.editMode) {
        if (/^https?:\/\//i.test(this.settings.linkUrl)) {
          window.open(this.settings.linkUrl, "_blank");
        } else if (this.$router) {
          this.$router.push(this.settings.linkUrl).catch(() => {});
        }
      }
    },
    buildParamsObject() {
      const out = {};
      (this.settings.apiParams || []).forEach((p) => {
        if (p && p.name) out[p.name] = p.value;
      });
      if (this.settings.apiFilters && this.settings.apiFilters.length) {
        out.filters = this.settings.apiFilters
          .filter((f) => f.field && f.values && f.values.length)
          .map((f) => ({
            field: f.field,
            condition: f.condition || "include",
            values: f.values,
          }));
      }
      return out;
    },
    fetchData() {
      if (this.editMode) return; // avoid hitting APIs in the builder preview
      const endpoint = this.settings.apiEndpoint;
      if (!endpoint) return;

      const method = (this.settings.apiMethod || "POST").toUpperCase();
      const params = this.buildParamsObject();
      const request =
        method === "GET"
          ? axios.get(endpoint, { params })
          : axios.post(endpoint, params);

      this.isLoading = true;
      request
        .then((resp) => {
          const raw = Array.isArray(resp.data)
            ? resp.data
            : resp.data && resp.data.Status === "OK"
            ? resp.data.Data
            : null;
          this.apiSegments = this.mapToSegments(raw);
          this.isLoading = false;
        })
        .catch((err) => {
          this.isLoading = false;
          if (err.response && err.response.status === 401) {
            this.$emit("sessionExpired", err);
          } else {
            console.error("DonutChart fetch failed", err);
          }
        });
    },
    mapToSegments(raw) {
      if (!Array.isArray(raw)) return [];
      const m = this.settings.segmentMapping || {};
      const labelField = m.labelField || "label";
      const valueField = m.valueField || "value";
      const colourField = m.colourField || "colour";
      const overrides = this.settings.segmentColours || [];

      return raw.map((row, i) => {
        const label = row[labelField];
        const value = Number(row[valueField]) || 0;
        const override = overrides.find((o) => o.label === label);
        const color =
          (override && override.colour) ||
          row[colourField] ||
          DEFAULT_PALETTE[i % DEFAULT_PALETTE.length];
        return { label, value, color };
      });
    },
  },
};
</script>

<style scoped lang="scss">
.donut-chart-card {
  font-family: 'DM Sans', sans-serif !important;
  ::v-deep *:not(.v-icon):not(.v-icon *) { font-family: inherit !important; }
  h1, h2, ::v-deep h1, ::v-deep h2 {
    font-family: 'DM Serif Display', serif !important;
  }

  border-radius: 16px !important;
  padding: 20px 24px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.04) !important;
  border: 1px solid #eef0f4;

  .card-title {
    font-size: 18px;
    font-weight: 700;
    margin: 0 0 16px 0;
    color: #1f2937;
  }

  .body {
    display: flex;
    align-items: stretch;
    gap: 24px;
    min-height: 200px;
  }

  .chart-holder {
    position: relative;
    width: 180px;
    height: 180px;
    flex-shrink: 0;
    align-self: center;

    .centre {
      position: absolute;
      inset: 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      pointer-events: none;

      .centre-value {
        font-size: 28px;
        font-weight: 700;
        line-height: 1;
        color: #1f2937;
      }
      .centre-label {
        font-size: 12px;
        color: #6b7280;
        margin-top: 4px;
        text-align: center;
      }
    }
  }

  .legend-column {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    min-width: 0;
  }

  .legend {
    list-style: none;
    padding: 0;
    margin: 0;
    font-size: 14px;
    color: #374151;
    flex: 1;

    li {
      display: flex;
      align-items: flex-start;
      margin-bottom: 6px;
      line-height: 1.3;

      .swatch {
        display: inline-block;
        width: 10px;
        height: 10px;
        border-radius: 50%;
        margin-top: 5px;
        margin-right: 8px;
        flex-shrink: 0;
      }

      .legend-text {
        strong { font-weight: 700; margin-right: 2px; }
        .count { color: #6b7280; margin-right: 4px; }
      }

      &.empty {
        color: #9ca3af;
        font-style: italic;
      }
    }
  }

  .footer {
    margin-top: 12px;
    text-align: left;
  }

  .view-link {
    color: #2cd946;
    font-size: 14px;
    font-weight: 600;
    text-decoration: none;
    &:hover { text-decoration: underline; }
  }
}
</style>
